import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, HStack, VStack, Box, Text, Spinner,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb, NumberInput, NumberInputField,
  Switch, FormControl, FormLabel,
  useToast,
} from '@chakra-ui/react';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const DEFAULT_SECTION_SPACING = 20;
const DEFAULT_FOOTER_MARGIN_TOP = 60;
const DEFAULT_PAGE_TOP_MARGIN = 8;
const DEFAULT_FOOTER_OWN_PAGE = false;
const DEFAULT_HIDE_ALL_PRICES = false;

// Parse Content-Disposition for either RFC 5987 `filename*=UTF-8''…` (preferred,
// preserves non-ASCII characters) or the plain `filename=` fallback. Returns
// null when the header is absent or unparseable.
function parseFilenameFromDisposition(cd) {
  if (!cd) return null;
  const star = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (star) {
    try { return decodeURIComponent(star[1]); } catch (_) { /* fall through */ }
  }
  const plain = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
  return plain ? plain[1].trim() : null;
}
const SECTION_SLIDER_MIN = -30;
const SECTION_SLIDER_MAX = 80;
const FOOTER_SLIDER_MIN = 0;
const FOOTER_SLIDER_MAX = 120;
const PAGE_TOP_SLIDER_MIN = 0;
const PAGE_TOP_SLIDER_MAX = 100;

export default function PDFDownloadModal({ isOpen, onClose, proposalId }) {
  const { t, currentLanguage } = useLanguage();
  const toast = useToast();
  const [sectionSpacing, setSectionSpacing] = useState(DEFAULT_SECTION_SPACING);
  const [footerMarginTop, setFooterMarginTop] = useState(DEFAULT_FOOTER_MARGIN_TOP);
  const [pageTopMargin, setPageTopMargin] = useState(DEFAULT_PAGE_TOP_MARGIN);
  const [footerOwnPage, setFooterOwnPage] = useState(DEFAULT_FOOTER_OWN_PAGE);
  const [hideAllPrices, setHideAllPrices] = useState(DEFAULT_HIDE_ALL_PRICES);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const debounceRef = useRef(null);
  const previewUrlRef = useRef('');

  const buildOverrides = useCallback((sectionPx, footerPx, pageTopPx, ownPage, hidePrices) => ({
    spacing: {
      sectionMarginTop: `${sectionPx}px`,
      footerMarginTop: `${footerPx}px`,
    },
    layout: {
      pageTopMargin: `${pageTopPx}px`,
      footerOwnPage: !!ownPage,
      hideAllPrices: !!hidePrices,
    },
  }), []);

  useEffect(() => {
    if (!isOpen || !proposalId) return;
    setInitialLoaded(false);
    const parsePx = (v, fallback) => {
      const n = v ? parseInt(String(v), 10) : fallback;
      return Number.isFinite(n) ? n : fallback;
    };
    api.get(`/api/proposals/${proposalId}`)
      .then(res => {
        const p = res.data?.data?.proposal || res.data?.data;
        const stored = p?.pdf_print_overrides;
        let parsed = null;
        if (stored) {
          try { parsed = typeof stored === 'string' ? JSON.parse(stored) : stored; } catch (_) { parsed = null; }
        }
        setSectionSpacing(parsePx(parsed?.spacing?.sectionMarginTop, DEFAULT_SECTION_SPACING));
        setFooterMarginTop(parsePx(parsed?.spacing?.footerMarginTop, DEFAULT_FOOTER_MARGIN_TOP));
        setPageTopMargin(parsePx(parsed?.layout?.pageTopMargin, DEFAULT_PAGE_TOP_MARGIN));
        setFooterOwnPage(
          typeof parsed?.layout?.footerOwnPage === 'boolean'
            ? parsed.layout.footerOwnPage
            : DEFAULT_FOOTER_OWN_PAGE
        );
        setHideAllPrices(
          typeof parsed?.layout?.hideAllPrices === 'boolean'
            ? parsed.layout.hideAllPrices
            : DEFAULT_HIDE_ALL_PRICES
        );
      })
      .catch(() => {
        setSectionSpacing(DEFAULT_SECTION_SPACING);
        setFooterMarginTop(DEFAULT_FOOTER_MARGIN_TOP);
        setPageTopMargin(DEFAULT_PAGE_TOP_MARGIN);
        setFooterOwnPage(DEFAULT_FOOTER_OWN_PAGE);
        setHideAllPrices(DEFAULT_HIDE_ALL_PRICES);
      })
      .finally(() => setInitialLoaded(true));
  }, [isOpen, proposalId]);

  const refreshPreview = useCallback((sectionPx, footerPx, pageTopPx, ownPage, hidePrices) => {
    if (!proposalId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Show loader immediately so the user knows we registered their change,
    // even though the network request is debounced.
    setPreviewLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.post(
          `/api/proposals/${proposalId}/preview-pdf`,
          { style_config: buildOverrides(sectionPx, footerPx, pageTopPx, ownPage, hidePrices), lang: currentLanguage },
          { responseType: 'blob' }
        );
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        if (previewUrlRef.current) window.URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      } catch (_) {
        setPreviewUrl('');
      } finally {
        setPreviewLoading(false);
      }
    }, 700);
  }, [proposalId, currentLanguage, buildOverrides]);

  useEffect(() => {
    if (isOpen && initialLoaded) {
      refreshPreview(sectionSpacing, footerMarginTop, pageTopMargin, footerOwnPage, hideAllPrices);
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [isOpen, initialLoaded, sectionSpacing, footerMarginTop, pageTopMargin, footerOwnPage, hideAllPrices, refreshPreview]);

  // Clean up blob URL on unmount or close
  useEffect(() => {
    if (!isOpen && previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
      setPreviewUrl('');
    }
  }, [isOpen]);

  useEffect(() => () => {
    if (previewUrlRef.current) window.URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const handleReset = () => {
    setSectionSpacing(DEFAULT_SECTION_SPACING);
    setFooterMarginTop(DEFAULT_FOOTER_MARGIN_TOP);
    setPageTopMargin(DEFAULT_PAGE_TOP_MARGIN);
    setFooterOwnPage(DEFAULT_FOOTER_OWN_PAGE);
    setHideAllPrices(DEFAULT_HIDE_ALL_PRICES);
  };

  // Build the persisted overrides: include ONLY the keys that differ from the
  // modal's defaults. This stops "Save & Download" from baking a default-valued
  // override into the proposal, which would later shadow any template-level
  // change the user makes in settings/pdf. If every slider is at default,
  // pdf_print_overrides is cleared entirely (null) so the template wins.
  const buildPersistedOverrides = useCallback(() => {
    const spacing = {};
    const layout = {};
    if (sectionSpacing !== DEFAULT_SECTION_SPACING) spacing.sectionMarginTop = `${sectionSpacing}px`;
    if (footerMarginTop !== DEFAULT_FOOTER_MARGIN_TOP) spacing.footerMarginTop = `${footerMarginTop}px`;
    if (pageTopMargin !== DEFAULT_PAGE_TOP_MARGIN) layout.pageTopMargin = `${pageTopMargin}px`;
    if (footerOwnPage !== DEFAULT_FOOTER_OWN_PAGE) layout.footerOwnPage = footerOwnPage;
    if (hideAllPrices !== DEFAULT_HIDE_ALL_PRICES) layout.hideAllPrices = hideAllPrices;
    const out = {};
    if (Object.keys(spacing).length) out.spacing = spacing;
    if (Object.keys(layout).length) out.layout = layout;
    return Object.keys(out).length ? out : null;
  }, [sectionSpacing, footerMarginTop, pageTopMargin, footerOwnPage, hideAllPrices]);

  const handleSaveAndDownload = async () => {
    if (!proposalId) return;
    setDownloading(true);
    try {
      const persisted = buildPersistedOverrides();
      await api.put(`/api/proposals/${proposalId}/pdf-overrides`, {
        pdf_print_overrides: persisted,
      });
      const res = await api.get(
        `/api/proposals/${proposalId}/pdf?lang=${currentLanguage}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = parseFilenameFromDisposition(res.headers?.['content-disposition'])
        || `proposal_${proposalId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      toast({ title: t('common.error') || 'Error', status: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('proposal.pdf_download_modal.title', 'PDF Download Preview')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <HStack align="start" spacing={6} h="70vh">
            <VStack align="stretch" spacing={4} minW="280px" maxW="320px">
              <Box>
                <Text fontWeight="bold" mb={2}>
                  {t('proposal.pdf_download_modal.page_top_margin', 'Page Top Margin')}
                </Text>
                <HStack>
                  <Slider
                    value={pageTopMargin}
                    onChange={setPageTopMargin}
                    min={PAGE_TOP_SLIDER_MIN}
                    max={PAGE_TOP_SLIDER_MAX}
                    step={1}
                    flex="1"
                  >
                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <NumberInput
                    value={pageTopMargin}
                    onChange={(_, n) => setPageTopMargin(Number.isFinite(n) ? n : 0)}
                    min={PAGE_TOP_SLIDER_MIN}
                    max={PAGE_TOP_SLIDER_MAX}
                    size="sm"
                    maxW="80px"
                  >
                    <NumberInputField />
                  </NumberInput>
                  <Text fontSize="sm" color="gray.500">px</Text>
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {t('proposal.pdf_download_modal.page_top_margin_hint',
                    'Top whitespace on every page. Reduce to gain vertical space.')}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>
                  {t('proposal.pdf_download_modal.section_spacing', 'Section Spacing')}
                </Text>
                <HStack>
                  <Slider
                    value={sectionSpacing}
                    onChange={setSectionSpacing}
                    min={SECTION_SLIDER_MIN}
                    max={SECTION_SLIDER_MAX}
                    step={1}
                    flex="1"
                  >
                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <NumberInput
                    value={sectionSpacing}
                    onChange={(_, n) => setSectionSpacing(Number.isFinite(n) ? n : 0)}
                    min={SECTION_SLIDER_MIN}
                    max={SECTION_SLIDER_MAX}
                    size="sm"
                    maxW="80px"
                  >
                    <NumberInputField />
                  </NumberInput>
                  <Text fontSize="sm" color="gray.500">px</Text>
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {t('proposal.pdf_download_modal.section_spacing_hint',
                    'Reduce to pull totals onto the current page when they overflow.')}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>
                  {t('proposal.pdf_download_modal.footer_margin_top', 'Footer Top Margin')}
                </Text>
                <HStack>
                  <Slider
                    value={footerMarginTop}
                    onChange={setFooterMarginTop}
                    min={FOOTER_SLIDER_MIN}
                    max={FOOTER_SLIDER_MAX}
                    step={1}
                    flex="1"
                  >
                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                    <SliderThumb />
                  </Slider>
                  <NumberInput
                    value={footerMarginTop}
                    onChange={(_, n) => setFooterMarginTop(Number.isFinite(n) ? n : 0)}
                    min={FOOTER_SLIDER_MIN}
                    max={FOOTER_SLIDER_MAX}
                    size="sm"
                    maxW="80px"
                  >
                    <NumberInputField />
                  </NumberInput>
                  <Text fontSize="sm" color="gray.500">px</Text>
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {t('proposal.pdf_download_modal.footer_margin_hint',
                    'Reduce the gap between GESAMTSUMME and the disclaimer footer.')}
                </Text>
              </Box>

              <Box>
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel htmlFor="footer-own-page-toggle" mb="0" fontWeight="bold">
                    {t('proposal.pdf_download_modal.footer_own_page', 'Keep signature page separate')}
                  </FormLabel>
                  <Switch
                    id="footer-own-page-toggle"
                    isChecked={footerOwnPage}
                    onChange={(e) => setFooterOwnPage(e.target.checked)}
                    colorScheme="blue"
                  />
                </FormControl>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {t('proposal.pdf_download_modal.footer_own_page_hint',
                    'Force the signature & company footer onto a dedicated last page so the layout stays fixed when you adjust spacing above.')}
                </Text>
              </Box>

              <Box>
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel htmlFor="hide-all-prices-toggle" mb="0" fontWeight="bold">
                    {t('proposal.pdf_download_modal.hide_all_prices', 'Show only grand total')}
                  </FormLabel>
                  <Switch
                    id="hide-all-prices-toggle"
                    isChecked={hideAllPrices}
                    onChange={(e) => setHideAllPrices(e.target.checked)}
                    colorScheme="blue"
                  />
                </FormControl>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {t('proposal.pdf_download_modal.hide_all_prices_hint',
                    'Hide every line price and section subtotal; only the final GESAMTSUMME stays visible.')}
                </Text>
              </Box>

              <Button size="sm" variant="outline" onClick={handleReset}>
                {t('common.reset', 'Reset to Default')}
              </Button>
            </VStack>
            <Box flex="1" h="100%" border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" position="relative">
              {previewUrl ? (
                <iframe
                  title="pdf-preview"
                  src={previewUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 0,
                    opacity: previewLoading ? 0.35 : 1,
                    transition: 'opacity 150ms ease',
                    pointerEvents: previewLoading ? 'none' : 'auto',
                  }}
                />
              ) : (
                <Box p={6} color="gray.500">
                  {previewLoading
                    ? (t('common.loading', 'Loading...'))
                    : (t('proposal.pdf_download_modal.preview_empty', 'Adjust settings to see preview'))}
                </Box>
              )}
              {previewLoading && (
                <Box
                  position="absolute"
                  inset={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  pointerEvents="none"
                  zIndex={2}
                >
                  <VStack
                    spacing={3}
                    bg="whiteAlpha.900"
                    px={6}
                    py={4}
                    borderRadius="md"
                    boxShadow="md"
                  >
                    <Spinner size="lg" thickness="3px" color="blue.500" />
                    <Text fontSize="sm" color="gray.700">
                      {t('proposal.pdf_download_modal.regenerating', 'Regenerating preview…')}
                    </Text>
                  </VStack>
                </Box>
              )}
            </Box>
          </HStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={downloading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button colorScheme="blue" onClick={handleSaveAndDownload} isLoading={downloading}>
            {t('proposal.pdf_download_modal.save_and_download', 'Save & Download')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
